import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { brl } from "@/lib/format";
import { Card, SaveBtn, Empty } from "./ui";
import { SmartImage } from "../public/media";

export default function ConversionModule({ rid, data, reload }) {
  const [ed, setEd] = useState(null);
  const [q, setQ] = useState("");
  const nameOf = (id) => data.products.find((p) => p.id === id)?.name;
  const list = data.products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="conversion-module">
      <Card title="Venda Guiada / Upsell" desc="Escolha produtos recomendados para cada item e defina onde exibi-los: na página do produto, no carrinho ou em ambos.">
        <div className="flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 mb-3">
          <Search size={16} className="text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="conv-search" placeholder="Buscar produto..." className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        {data.products.length === 0 && <Empty>Cadastre produtos primeiro.</Empty>}
        <div className="space-y-2">
          {list.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-1">
              <SmartImage src={p.image} className="h-10 w-10 rounded-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{p.name}</p>
                <p className="text-xs text-zinc-500 truncate">{p.upsell_product_ids?.length ? `${p.upsell_product_ids.length} recomendado(s): ${p.upsell_product_ids.map(nameOf).filter(Boolean).slice(0, 2).join(", ")}` : "Sem upsell"}</p>
              </div>
              <button onClick={() => setEd(p)} data-testid={`conv-edit-${p.id}`} className="text-xs font-semibold bg-white/10 px-3 py-1.5 rounded-full">Configurar</button>
            </div>
          ))}
        </div>
      </Card>
      {ed && <UpsellEditor rid={rid} product={ed} products={data.products} onClose={() => setEd(null)} onSaved={() => { setEd(null); reload(); }} />}
    </div>
  );
}

function UpsellEditor({ rid, product, products, onClose, onSaved }) {
  const [ids, setIds] = useState(product.upsell_product_ids || []);
  const [loc, setLoc] = useState(product.upsell_location || "both");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  // eligible: not itself, active, not sold out
  const eligible = useMemo(() => products.filter((p) => p.id !== product.id && p.available !== false && !p.sold_out && (!q || p.name.toLowerCase().includes(q.toLowerCase()))), [products, product, q]);
  const toggle = (id) => setIds((p) => {
    if (p.includes(id)) return p.filter((x) => x !== id);
    if (p.length >= 5) { toast.warning("Recomende até 5 produtos"); return p; }
    return [...p, id];
  });
  const save = async () => {
    setLoading(true);
    try { await api.put(`/restaurants/${rid}/products/${product.id}`, { upsell_product_ids: ids, upsell_location: loc }); toast.success("Upsell salvo"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-3 py-6" onClick={onClose}>
      <div className="w-full max-w-md bg-[#141417] border border-white/10 rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="upsell-editor">
        <div className="flex items-center justify-between mb-4"><h3 className="font-head text-lg font-extrabold truncate">Upsell — {product.name}</h3><button onClick={onClose}><X size={20} className="text-zinc-500" /></button></div>
        <label className="text-xs font-semibold text-zinc-400">Onde exibir</label>
        <div className="grid grid-cols-3 gap-2 mt-1.5 mb-4">
          {[["produto", "Produto"], ["carrinho", "Carrinho"], ["both", "Ambos"]].map(([v, l]) => (
            <button key={v} onClick={() => setLoc(v)} data-testid={`conv-loc-${v}`} className={`text-sm font-semibold py-2 rounded-lg ${loc === v ? "hmmm-gradient text-white" : "bg-white/5 text-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-zinc-400">Recomendados (ideal 3–5)</label>
          <span className="text-[11px] text-zinc-500">{ids.length}/5</span>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 mb-2">
          <Search size={15} className="text-zinc-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <div className="max-h-64 overflow-y-auto no-scrollbar space-y-1">
          {eligible.map((p) => (
            <label key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
              <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`conv-item-${p.id}`} />
              <SmartImage src={p.image} className="h-8 w-8 rounded-lg" />
              <span className="flex-1 text-sm truncate">{p.name}</span>
              <span className="text-xs text-zinc-500">{brl(p.promo_price || p.price)}</span>
            </label>
          ))}
          {eligible.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center">Nenhum produto elegível.</p>}
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/10"><button onClick={onClose} className="rounded-full bg-white/10 px-5 py-2.5 font-semibold">Cancelar</button><SaveBtn onClick={save} loading={loading} tid="conv-save" /></div>
      </div>
    </div>
  );
}
